import {
  isTerminalViolationStatus,
  type AppealResolution,
  type ViolationDetail,
} from '@aysar/contracts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { useModal } from '@/shared/hooks/useModal/useModal';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ModalData } from '@/shared/store/reducers/modal/modal.types';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import { VIOLATION_MODALS } from '../../config/violations-modals.config';
import {
  useDecideAppealMutation,
  useDismissViolationMutation,
  useViolationQuery,
} from '../../services/violations/violations.api';
import { violationErrorCopy } from '../../utils/violation-error/violation-error.util';

export interface UseViolationReviewModalLogic {
  /** `null` when the payload carried no usable id — the dialog then draws nothing. */
  violationId: string | null;
  violation: ViolationDetail | null;
  isLoading: boolean;
  /** The case itself could not be read. Distinct from a refused decision. */
  loadError: ApiError | null;
  reload: () => void;
  /** The operator holds `violations.manage` and may act on this case. */
  canDecide: boolean;
  /** Waiting on a human: the two consequences are offered. */
  isQueued: boolean;
  /** The member has appealed: the appeal verdict is offered instead. */
  isAppealed: boolean;
  /** Nothing more can happen to it — the dialog is a record. */
  isDecided: boolean;
  /** Opens the right confirmation for this row. Does nothing before it arrives. */
  openEnforce: () => void;
  note: string;
  setNote: (value: string) => void;
  resolution: AppealResolution | null;
  setResolution: (value: AppealResolution) => void;
  canDismiss: boolean;
  canDecideAppeal: boolean;
  isSaving: boolean;
  dismiss: () => Promise<void>;
  decideAppeal: () => Promise<void>;
  /** The api's refusal, already in this module's words. */
  error: string | null;
  /** A colleague decided first: the answer is re-read, never retry. */
  isConflict: boolean;
  /** The case is not there any more: there is nothing to go back and read. */
  isGone: boolean;
}

/**
 * Reading one violation, and deciding it.
 *
 * ## It reads its own copy, and that is the point
 *
 * The payload is the violation **id** alone — `ModalData` is serializable-only,
 * and a dialog carrying a snapshot of the row would be deciding a case the
 * queue's 30-second poll may already have moved on from. Reading its own entry
 * is also the only way to get the **message body**: it is deliberately absent
 * from the list so member content never enters a paged, cacheable response, and
 * this single-row read under `violations.review` is the narrowest place a
 * moderator can see the thing they are judging (N13 leaves no other surface).
 *
 * ## Reading and deciding are one dialog, gated at the CONTROL
 *
 * `violations.review` opens it; `violations.manage` decides. Two dialogs would
 * leave the read-only reviewer nowhere to read, and hiding this one from them
 * would hide the queue's only legible surface (P10.6j).
 *
 * ## The consequence is confirmed one level up, and there are two of them
 *
 * Nothing here enforces. `openEnforce` opens a confirmation **chosen from the
 * row**: `CLASSIFY` when the classifier named no rule (the moderator must supply
 * one, and the api 422s without it) and `ENFORCE` otherwise. Making that a
 * property of the row rather than a field in one dialog is what makes the 422
 * unreachable rather than merely unlikely.
 *
 * ## A conflict is not a failure
 *
 * The queue polls, so two moderators landing on one row is ordinary, and the one
 * who got there first is **right**. `isConflict` lets the dialog offer a re-read
 * instead of a retry the api would refuse again, and the dialog stays open,
 * because a toast would take the news away before it was read.
 */
export function useViolationReviewModalLogic(
  modalData: ModalData | undefined,
  onClose: () => void,
): UseViolationReviewModalLogic {
  const { t } = useTranslation('violations');
  const toast = useToast();
  const { can } = useHasPermission();
  const { openModal } = useModal();

  const [note, setNote] = useState('');
  const [resolution, setResolution] = useState<AppealResolution | null>(null);
  const [refusal, setRefusal] = useState<ApiError | null>(null);

  const violationId = readViolationId(modalData);
  const query = useViolationQuery(violationId ?? '', { skip: violationId === null });
  const [dismissViolation, { isLoading: isDismissing }] = useDismissViolationMutation();
  const [decideAppealMutation, { isLoading: isDeciding }] = useDecideAppealMutation();

  const violation = query.data ?? null;
  const canDecide = can('violations.manage');
  const isQueued = violation?.status === 'queued_for_review';
  const isAppealed = violation?.status === 'appealed';
  const isDecided = violation !== null && isTerminalViolationStatus(violation.status);
  const isSaving = isDismissing || isDeciding;

  const openEnforce = useCallback(() => {
    if (violation === null) {
      return;
    }

    openModal(
      // The four type fields go null together, so `violationTypeKey` alone is a
      // sound test for "the classifier judged nothing here".
      violation.violationTypeKey === null ? VIOLATION_MODALS.CLASSIFY : VIOLATION_MODALS.ENFORCE,
      { violationId: violation.id },
    );
  }, [openModal, violation]);

  const dismiss = useCallback(async () => {
    if (violationId === null) return;

    setRefusal(null);

    try {
      await dismissViolation({ id: violationId, note: writtenNote(note) }).unwrap();
      toast.success(t('review.dismiss.done'));
      onClose();
    } catch (caught) {
      setRefusal(toApiError(caught));
    }
  }, [dismissViolation, note, onClose, t, toast, violationId]);

  const decideAppeal = useCallback(async () => {
    if (violationId === null || resolution === null) return;

    setRefusal(null);

    try {
      await decideAppealMutation({
        id: violationId,
        resolution,
        note: writtenNote(note),
      }).unwrap();
      toast.success(t('review.appeal.done'));
      onClose();
    } catch (caught) {
      setRefusal(toApiError(caught));
    }
  }, [decideAppealMutation, note, onClose, resolution, t, toast, violationId]);

  const copy = violationErrorCopy(refusal);

  return {
    violationId,
    violation,
    isLoading: query.isLoading,
    loadError: query.error === undefined ? null : toApiError(query.error),
    reload: () => {
      void query.refetch();
    },
    canDecide,
    isQueued,
    isAppealed,
    isDecided,
    openEnforce,
    note,
    setNote,
    resolution,
    setResolution,
    canDismiss: canDecide && isQueued && !isSaving,
    canDecideAppeal: canDecide && isAppealed && resolution !== null && !isSaving,
    isSaving,
    dismiss,
    decideAppeal,
    error: refusal === null ? null : t(copy === null ? 'error.generic' : copy.i18nKey),
    isConflict: copy?.isConflict ?? false,
    isGone: copy?.isGone ?? false,
  };
}

/**
 * A note the moderator actually wrote, or `null`.
 *
 * Never `''`: both note schemas trim and nullable the field, so an empty string
 * would be a value written onto a record deliberately left blank — and a record
 * that says "note: (empty)" reads differently from one that says nothing.
 */
function writtenNote(note: string): string | null {
  const trimmed = note.trim();

  return trimmed === '' ? null : trimmed;
}

/**
 * Narrows the modal payload.
 *
 * `ModalData` is `Record<string, unknown>` by design — it survives a reload and a
 * serialization boundary — so the field is checked rather than cast. A dialog
 * that trusted the payload would decide whatever id happened to be in it.
 */
function readViolationId(data: ModalData | undefined): string | null {
  if (data === undefined) return null;

  const value = data['violationId'];

  return typeof value === 'string' && value !== '' ? value : null;
}
